import apiClient from './api';

export const shopService = {
  // Register new shop
  registerShop: async (shopData) => {
    return await apiClient.post('/shops/register', shopData);
  },

  // Get all shops (super admin) or own shop (shop admin)
  getShops: async () => {
    return await apiClient.get('/shops');
  },

  // Get specific shop by ID (public)
  getShopById: async (shopId) => {
    return await apiClient.get(`/shops/${shopId}`);
  },

  // Update shop
  updateShop: async (shopId, updates) => {
    return await apiClient.put(`/shops/${shopId}`, updates);
  },

  // Add product to shop
  addProduct: async (shopId, productData) => {
    return await apiClient.post(`/shops/${shopId}/products`, productData);
  },

  // Update product
  updateProduct: async (shopId, productId, updates) => {
    return await apiClient.put(`/shops/${shopId}/products/${productId}`, updates);
  },

  // Delete product
  deleteProduct: async (shopId, productId) => {
    return await apiClient.delete(`/shops/${shopId}/products/${productId}`);
  },

  // Toggle shop active status
  toggleShopStatus: async (shopId, isActive) => {
    return await apiClient.put(`/shops/${shopId}/toggle-status`, { isActive });
  },

  // Ad image management
  uploadAdImage: async (shopId, imageFile) => {
    const formData = new FormData();
    formData.append('adImage', imageFile);

    return await apiClient.post(`/shops/${shopId}/ads/upload`, formData);
  },

  getShopAds: async (shopId) => {
    return await apiClient.get(`/shops/${shopId}/ads`);
  },

  deleteAdImage: async (shopId, imageUrl) => {
    return await apiClient.delete(`/shops/${shopId}/ads`, { data: { imageUrl } });
  }
};

export const orderService = {
  // Create order
  createOrder: async (orderData) => {
    // Extract language code if provided (e.g., 'en-IN' -> 'en')
    if (orderData.selectedLanguage) {
      orderData.language = orderData.selectedLanguage.split('-')[0];
      delete orderData.selectedLanguage;
    }
    return await apiClient.post('/orders/create', orderData);
  },

  // Parse order text for preview
  parseOrder: async (shopId, orderText, selectedLanguage = 'en-IN') => {
    // Extract language code (e.g., 'en-IN' -> 'en', 'hi-IN' -> 'hi')
    const language = selectedLanguage.split('-')[0];
    return await apiClient.post('/orders/parse', { shopId, orderText, language });
  },

  // Get orders for shop
  getShopOrders: async (shopId, params = {}) => {
    return await apiClient.get(`/orders/shop/${shopId}`, { params });
  },

  // Get specific order
  getOrderById: async (orderId) => {
    return await apiClient.get(`/orders/${orderId}`);
  }
};

// Voice recognition now uses browser's Web Speech API (no backend needed)
export const voiceService = {
  // Get supported languages for Web Speech API
  getSupportedLanguages: () => {
    return {
      success: true,
      languages: [
        { code: 'en-IN', name: 'English (India)' },
        { code: 'hi-IN', name: 'Hindi' },
        { code: 'te-IN', name: 'Telugu' },
        { code: 'ta-IN', name: 'Tamil' },
        { code: 'en-US', name: 'English (US)' }
      ]
    };
  }
};

export const dashboardService = {
  // Shop admin dashboards
  getDailyDashboard: async (shopId, date) => {
    const params = date ? { date } : {};
    return await apiClient.get(`/dashboard/shop/${shopId}/daily`, { params });
  },

  getMonthlyDashboard: async (shopId, year, month) => {
    const params = { year, month };
    return await apiClient.get(`/dashboard/shop/${shopId}/monthly`, { params });
  },

  getYearlyDashboard: async (shopId, year) => {
    const params = { year };
    return await apiClient.get(`/dashboard/shop/${shopId}/yearly`, { params });
  },

  // Super admin dashboard
  getSuperAdminOverview: async () => {
    return await apiClient.get('/dashboard/super-admin/overview');
  }
};

export const authService = {
  // Create user
  createUser: async (userData) => {
    return await apiClient.post('/auth/create-user', userData);
  },

  // Get user profile
  getProfile: async () => {
    return await apiClient.get('/auth/profile');
  },

  // Verify OTP
  verifyOTP: async (mobile, uid) => {
    return await apiClient.post('/auth/verify-otp', { mobile, uid });
  }
};
